import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import EmployeesApi from '../../../api/employees';

const STATUS_OPTIONS = [
  { value: 100, label: 'לא פעיל (100)' },
  { value: 200, label: 'פעיל (200)' },
  { value: 300, label: 'חסום (300)' },
  { value: 400, label: 'עזב (400)' },
];

function normalizePhone(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 9 && digits.startsWith('5')) return '0' + digits;
  if (digits.length === 10 && digits.startsWith('0')) return digits;
  return null;
}

function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) return resolve([]);
        const headers = rows[0].map((h) => String(h).trim());
        const phoneCol = headers.findIndex((h) =>
          ['נייד', 'טלפון', 'phone', 'נייד/טלפון'].includes(h)
        );
        const govIdCol = headers.findIndex((h) =>
          ['ת.ז.', 'תז', 'תעודת זהות', 'govid', 'gov_id', 'ת.ז'].includes(h.toLowerCase())
        );
        const firstNameCol = headers.findIndex((h) => ['שם פרטי', 'firstName', 'first_name'].includes(h));
        const lastNameCol = headers.findIndex((h) => ['שם משפחה', 'lastName', 'last_name'].includes(h));
        const result = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.every((c) => c === undefined || c === null || c === '')) continue;
          const phoneNumber = phoneCol >= 0 ? normalizePhone(row[phoneCol]) : null;
          const govId = govIdCol >= 0 ? String(row[govIdCol] ?? '').trim() : null;
          const firstName = firstNameCol >= 0 ? String(row[firstNameCol] ?? '').trim() : '';
          const lastName = lastNameCol >= 0 ? String(row[lastNameCol] ?? '').trim() : '';
          result.push({
            phoneNumber,
            govId: govId || null,
            firstName,
            lastName,
            rowIndex: i,
          });
        }
        resolve(result);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function defaultReasonFromFile(file) {
  if (!file) return '';
  const stem = file.name.replace(/\.(xlsx|xls|csv)$/i, '').trim();
  return stem || `leavers_${new Date().toISOString().slice(0, 10)}`;
}

const OUTCOME_BADGE = {
  updated: { colorScheme: 'green', label: 'עודכן' },
  updated_with_otp_warning: { colorScheme: 'yellow', label: 'עודכן (אזהרת OTP)' },
  already_at_target: { colorScheme: 'gray', label: 'לא שונה' },
  blocked_added: { colorScheme: 'blue', label: 'נוסף לחסומים' },
  already_blocked: { colorScheme: 'purple', label: 'כבר חסום' },
  blocked_by_pending: { colorScheme: 'red', label: 'בקשה בטיפול' },
  invalid: { colorScheme: 'orange', label: 'פרטים חסרים' },
  error: { colorScheme: 'red', label: 'שגיאה' },
};

function statusBadge(outcome) {
  const { colorScheme, label } = OUTCOME_BADGE[outcome] ?? { colorScheme: 'gray', label: outcome };
  return <Badge colorScheme={colorScheme}>{label}</Badge>;
}

function formatPendingApps(apps) {
  if (!apps?.length) return '';
  return apps
    .map((a) => {
      const date = a.createdDate ? new Date(a.createdDate).toLocaleString('he-IL') : '';
      return `#${a.id} סטטוס ${a.status} · ₪${a.amount} · ${date}`;
    })
    .join(' | ');
}

export default function BulkStatusChangeModal({ isOpen, onClose }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [targetStatus, setTargetStatus] = useState('');
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!reasonTouched) {
      setReason(defaultReasonFromFile(file));
    }
  }, [file, reasonTouched]);

  const reset = () => {
    setFile(null);
    setTargetStatus('');
    setReason('');
    setReasonTouched(false);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (!running) {
      reset();
      onClose();
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleRun = async () => {
    if (!file || !targetStatus || !reason.trim()) return;
    setRunning(true);
    setResults(null);

    let rows;
    try {
      rows = await parseExcel(file);
    } catch {
      alert('שגיאה בקריאת הקובץ');
      setRunning(false);
      return;
    }

    if (rows.length === 0) {
      alert('לא נמצאו שורות בקובץ');
      setRunning(false);
      return;
    }

    try {
      const response = await EmployeesApi.processLeavers({
        rows,
        targetStatus: Number(targetStatus),
        reason: reason.trim(),
      });
      setResults(response?.results ?? []);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'שגיאה';
      alert('שגיאה בעיבוד: ' + msg);
    }
    setRunning(false);
  };

  const summary = results
    ? {
        updated: results.filter((r) => r.outcome === 'updated' || r.outcome === 'updated_with_otp_warning').length,
        otp_warning: results.filter((r) => r.outcome === 'updated_with_otp_warning').length,
        skipped: results.filter((r) => r.outcome === 'already_at_target').length,
        blocked_added: results.filter((r) => r.outcome === 'blocked_added').length,
        already_blocked: results.filter((r) => r.outcome === 'already_blocked').length,
        pending_blocker: results.filter((r) => r.outcome === 'blocked_by_pending').length,
        invalid: results.filter((r) => r.outcome === 'invalid').length,
        error: results.filter((r) => r.outcome === 'error').length,
      }
    : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton position="absolute" left="8px" right="auto" top="8px" />
        <ModalHeader>שינוי סטטוס עובדים בכמות</ModalHeader>

        <ModalBody pb={4}>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>קובץ עובדים (Excel)</FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                isDisabled={running}
                p={1}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                הקובץ צריך לכלול עמודת "נייד" ו/או "ת.ז.". שורות שלא נמצאו יתווספו לרשימת החסומים.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>סטטוס יעד (לעובדים שנמצאו)</FormLabel>
              <Select
                placeholder="בחר סטטוס..."
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value)}
                isDisabled={running}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>סיבה (לרשומת החסומים)</FormLabel>
              <Input
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonTouched(true);
                }}
                isDisabled={running}
                placeholder="לדוגמה: shlomo_leavers_04-10-2026"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                נשמר בעמודת reason של blocked_user. אם לא תשנה — ייגזר משם הקובץ.
              </Text>
            </FormControl>

            {running && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  מעבד...
                </Text>
              </Box>
            )}

            {results && summary && (
              <Box>
                <Flex gap={3} mb={3} wrap="wrap">
                  <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
                    עודכנו: {summary.updated}
                    {summary.otp_warning > 0 ? ` (${summary.otp_warning} עם אזהרה)` : ''}
                  </Badge>
                  <Badge colorScheme="gray" fontSize="sm" px={2} py={1}>
                    לא שונה: {summary.skipped}
                  </Badge>
                  <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                    נוסף לחסומים: {summary.blocked_added}
                  </Badge>
                  <Badge colorScheme="purple" fontSize="sm" px={2} py={1}>
                    כבר חסום: {summary.already_blocked}
                  </Badge>
                  <Badge colorScheme="red" fontSize="sm" px={2} py={1}>
                    בקשה בטיפול: {summary.pending_blocker}
                  </Badge>
                  <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                    פרטים חסרים: {summary.invalid}
                  </Badge>
                  <Badge colorScheme="red" fontSize="sm" px={2} py={1}>
                    שגיאות: {summary.error}
                  </Badge>
                </Flex>

                <Box maxH="400px" overflowY="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th>עובד</Th>
                        <Th>User ID</Th>
                        <Th>תוצאה</Th>
                        <Th>פירוט</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {results.map((r, idx) => (
                        <Tr key={idx}>
                          <Td fontWeight={500}>{r.label}</Td>
                          <Td>{r.userId ?? '—'}</Td>
                          <Td>{statusBadge(r.outcome)}</Td>
                          <Td fontSize="xs" color="gray.600">
                            {r.message}
                            {r.pendingApps?.length ? ` · ${formatPendingApps(r.pendingApps)}` : ''}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleClose} mr={3} variant="ghost" isDisabled={running}>
            סגור
          </Button>
          <Button
            variant="blue"
            isLoading={running}
            loadingText="מעבד..."
            isDisabled={!file || !targetStatus || !reason.trim() || running}
            onClick={handleRun}
          >
            הפעל
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
